import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import ContactModal from '../components/ContactModal';

/**
 * History tab — shows email thread history + Clio logging status.
 */
export default function HistoryTab() {
    const { linkedMatter, userEmail } = useAddin();
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);

    useEffect(() => {
        if (!linkedMatter?.id) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        webhook.matterHistory({ userEmail, matterId: linkedMatter.id })
            .then(res => {
                if (isMounted) setTimeline(res.timeline || []);
            })
            .catch(err => {
                console.error("Failed to fetch history:", err);
                if (isMounted) setError(err.message);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [linkedMatter?.id, userEmail]);

    // Format relative time
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const diffMs = new Date() - date;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHrs / 24);

        if (diffHrs < 24) return diffHrs <= 0 ? 'Just now' : `${diffHrs}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    if (!linkedMatter) {
        return (
            <div className="addin-body" style={{ padding: '20px', textAlign: 'center', color: '#8a8a8a' }}>
                <p>No matter linked.</p>
            </div>
        );
    }

    return (
        <div className="addin-body">
            <div className="section-label">Matter Timeline</div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>Loading history...</div>
            ) : error ? (
                <div style={{ color: 'var(--orange)', padding: '10px' }}>Error: {error}</div>
            ) : timeline.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>No history found.</div>
            ) : (
                <div style={{ padding: '2px 0' }}>
                    {timeline.map((item) => {
                        let dotColor = 'grey';
                        let prefix = 'Activity';
                        let metaText = '';

                        if (item.type.includes('Communication')) {
                            dotColor = 'teal';
                            prefix = 'Communication';
                            if (item.metadata?.senders?.[0]?.name) {
                                const sender = item.metadata.senders[0];
                                if (sender.id) {
                                    metaText = (
                                        <span>
                                            From: <span className="link" onClick={() => { setSelectedContactId(sender.id); setShowContactModal(true); }}>{sender.name}</span>
                                        </span>
                                    );
                                } else {
                                    metaText = `From: ${sender.name}`;
                                }
                            }
                        } else if (item.type === 'TimeEntry') {
                            dotColor = 'orange';
                            prefix = 'Time Entry';
                            metaText = `${item.metadata.quantity}h logged`;
                        } else if (item.type === 'ExpenseEntry') {
                            dotColor = 'orange';
                            prefix = 'Expense';
                            metaText = `$${item.metadata.total} charged`;
                        }

                        return (
                            <div className="activity-item" key={item.id} style={{ marginBottom: '12px', alignItems: 'flex-start' }}>
                                <div className={`activity-dot ${dotColor}`} style={{ marginTop: '5px' }} />
                                <div className="activity-text" style={{ paddingRight: '12px' }}>
                                    <div><strong>{prefix}</strong> — {item.description}</div>
                                    {metaText && <div style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '2px' }}>{metaText}</div>}
                                </div>
                                <div className="activity-time" style={{ whiteSpace: 'nowrap', paddingTop: '1px' }}>{formatTime(item.date)}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            <ContactModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                contactId={selectedContactId}
            />
        </div>
    );
}
